import { useState, type ReactElement } from 'react';
import type { PlanDto } from '@gr/core/accounts/plans/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import { useConfig, useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { createPlanRequest, updatePlanRequest } from './model/plan-form';
import { PlanForm } from './PlanForm/PlanForm';
import { PlanTable } from './PlanTable/PlanTable';

/** Что открыто в диалоге: новый план или правка существующего. */
type Editing = { kind: 'new' } | { kind: 'edit'; plan: PlanDto } | null;

// PLAN-UC-1…3, PLAN-UC-1.CC-1, PLAN-RULE-2, PLAN-RULE-3, PLAN-RULE-5, DEMO-RULE-3
export function PlansAdminPage(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const readOnly = useConfig().demo.enabled;
  const plans = useResource(() => api.plans.list(), [api]);
  const [editing, setEditing] = useState<Editing>(null);
  const change = useAction((action: () => Promise<OperationResult<unknown>>) => action());

  const run = async (action: () => Promise<OperationResult<unknown>>): Promise<void> => {
    if ((await change.run(action)).success) await plans.reload();
  };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={t('plans.title')}
        subtitle={t('plans.subtitle')}
        actions={
          readOnly ? null : (
            <Button variant="primary" icon="plus" onClick={() => setEditing({ kind: 'new' })}>
              {t('plans.create')}
            </Button>
          )
        }
      />
      {readOnly ? <Alert tone="info">{t('plans.readOnly')}</Alert> : null}
      {change.error ? <Alert tone="danger">{te(change.error)}</Alert> : null}
      {plans.loading && !plans.data ? (
        <Loading label={t('common.loading')} />
      ) : plans.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void plans.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : (
        <PlanTable
          plans={plans.data ?? []}
          pending={change.pending}
          readOnly={readOnly}
          onEdit={(plan) => setEditing({ kind: 'edit', plan })}
          onMakeDefault={(plan) => void run(() => api.plans.makeDefault(plan.id))}
          onToggleArchive={(plan) => void run(() => api.plans.update(plan.id, { archived: !plan.archived }))}
        />
      )}
      {editing ? (
        <Dialog
          open
          title={editing.kind === 'new' ? t('plans.createTitle') : t('plans.editTitle', { name: editing.plan.name })}
          closeLabel={t('common.close')}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
              <Button variant="primary" type="submit" form="plan-form">
                {editing.kind === 'new' ? t('common.create') : t('common.save')}
              </Button>
            </>
          }
        >
          <PlanForm
            id="plan-form"
            plan={editing.kind === 'edit' ? editing.plan : undefined}
            onSubmit={async (values) => {
              const result = editing.kind === 'new' ? await api.plans.create(createPlanRequest(values)) : await api.plans.update(editing.plan.id, updatePlanRequest(values));
              if (result.success) {
                setEditing(null);
                await plans.reload();
              }
              return result;
            }}
          />
        </Dialog>
      ) : null}
    </div>
  );
}
